import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/slices/authSlice';
import { apiHelper } from '../utils/api';
import session from '../utils/session';
import useTrialStatus from '../hooks/useTrialStatus';
import useMaintenanceStatus from '../hooks/useMaintenanceStatus';
import useActivityTracker from '../hooks/useActivityTracker';

// Layout
import Navbar from '../components/Navbar/Navbar';
import Sidebar from '../components/Sidebar/Sidebar';
import TrialPlanModal from '../components/TrialPlanModal/TrialPlanModal';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay/TrialExpiredOverlay';
import MaintenanceOverlay from '../components/MaintenanceOverlay/MaintenanceOverlay';
import PlanUpgradeModal from '../components/PlanUpgradeModal/PlanUpgradeModal';

import PlanExpiryWarningModal, { WARNING_DAYS } from '../components/PlanExpiryWarningModal/PlanExpiryWarningModal';
import AnnouncementModal from '../components/AnnouncementModal';
import AiAssistant from '../components/AiAssistant/AiAssistant';
import ContactCenter from '../components/ContactCenter/ContactCenter';
import SmartBackHandler from '../components/SmartBackHandler/SmartBackHandler';
import { GOLD_ONLY_PAGES } from '../hooks/useTrialStatus';

// Public Pages
import Login from '../pages/Login';
import Register from '../pages/Register';
import ForgotPassword from '../pages/ForgotPassword';
import VerifyEmail from '../pages/VerifyEmail';

// Private Pages
import Dashboard from '../pages/Dashboard';
import Projects from '../pages/Projects';
import Event from '../pages/Event';
import Team from '../pages/Team';
import Services from '../pages/Services';
import Quotations from '../pages/Quotations';
import Billing from '../pages/Billing';
import Settings from '../pages/Settings';
import Policy from '../pages/Policy';
import Payment from '../pages/Payment';
import Note from '../pages/Note';
import ChecklistList from '../pages/ChecklistList';

// Admin Pages
import AdminLayout from '../pages/Admin/AdminLayout';
import AdminDashboard from '../pages/Admin/Dashboard';
import UserManagement from '../pages/Admin/UserManagement';
import PlanManagement from '../pages/Admin/PlanManagement';
import PlanSettings from '../pages/Admin/PlanSettings';
import AdminPayments from '../pages/Admin/Payments';
import Analytics from '../pages/Admin/Analytics';
import Security from '../pages/Admin/Security';
import SuperAdminManagement from '../pages/Admin/SuperAdminManagement';
import Announcements from '../pages/Admin/Announcements';
import Maintenance from '../pages/Admin/Maintenance';
import SiteSettings from '../pages/Admin/SiteSettings';
import AiAssistantSettings from '../pages/Admin/AiAssistantSettings';
import SignupPlanControl from '../pages/Admin/SignupPlanControl';

// Auth initializer component - checks if httpOnly cookie has a valid session on mount
const AuthInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await apiHelper.get('/auth/me');
        if (response) {
          const { _token, ...userData } = response;
          const appUser = {
            id: userData._id || userData.id || '',
            fullName: userData.name || userData.fullName || '',
            email: userData.email || '',
            avatar: userData.photoURL || null,
            plan: userData.plan || 'free',
            createdAt: userData.createdAt,
            role: userData.role || 'unknown',
            name: userData.name || userData.fullName || '',
            photoURL: userData.photoURL || null,
            status: userData.status || 'active',
          };
          dispatch(loginSuccess({ user: appUser, token: _token }));
        } else {
          session.clear();
        }
      } catch {
        session.clear();
      } finally {
        setInitializing(false);
      }
    };
    checkSession();
  }, [dispatch]);

  if (initializing) {
    return null; // or a loading spinner
  }

  return children;
};

// Private Route Component - Requires authentication
// Preserves push deep-link query (e.g. ?announcementId=, ?id=) through login redirect
const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search || ""}`} replace />;
  }
  
  return children;
};

// Admin Route Component - Requires admin or superadmin role
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Super Admin Route Component - Requires superadmin role
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'superadmin';
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
};

// Public Route Component - Redirects to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Gold Plan Route Component - Requires Gold plan for access
const GoldPlanRoute = ({ children, featureName }) => {
  const trialStatus = useTrialStatus();
  const [showUpgradeModal, setShowUpgradeModal] = useState(true);

  // If user has Gold plan, allow access
  if (trialStatus.isGoldPlan) {
    return children;
  }

  // Show upgrade modal for non-Gold users
  return (
    <>
      <div className="restricted-page-placeholder">
        <PlanUpgradeModal
          isOpen={showUpgradeModal}
          onClose={(isAction) => {
            setShowUpgradeModal(false);
            // Only redirect to dashboard if modal was closed manually (not by plan action)
            if (isAction !== true) {
              window.location.href = '/dashboard';
            }
          }}
          featureName={featureName}
          currentPlan={trialStatus.planTier}
        />
      </div>
    </>
  );
};

// Layout for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.settings || {});
  const location = useLocation();
  const trialStatus = useTrialStatus();
  const maintenanceStatus = useMaintenanceStatus();
  // instant low-latency presence tracking (open/close/heartbeat) tied to active session
  useActivityTracker();
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const isPrivilegedUser = user?.role === 'admin' || user?.role === 'superadmin';

  // Check if current page is the payment page
  const isPaymentPage = location.pathname === '/payment';

  useEffect(() => {
    // Determine if we should show the trial modal
    // It shows for users on 'free' or 'none' plan who haven't dismissed it this session
    const userPlan = String(user?.plan || 'free').toLowerCase();
    const hasPaidPlan = userPlan === 'silver' || userPlan === 'gold';
    const wasShown = sessionStorage.getItem('trial_modal_shown');

    if (!hasPaidPlan && !wasShown && !trialStatus.isExpired && !isPrivilegedUser) {
      setShowTrialModal(true);
    }
  }, [user, trialStatus.isExpired]);

  // Show plan expiry warning for Silver/Gold users at designated day milestones
  useEffect(() => {
    if (isPrivilegedUser) return;
    if (!trialStatus.hasPaidPlan || trialStatus.isExpired) return;

    const days = trialStatus.daysRemaining;
    if (!WARNING_DAYS.includes(days)) return;

    const userId = user?._id || user?.id || 'unknown';
    const storageKey = `plan_expiry_warned_${userId}_${days}d`;
    const alreadyShown = localStorage.getItem(storageKey);

    if (!alreadyShown) {
      setShowExpiryWarning(true);
    }
  }, [trialStatus.daysRemaining, trialStatus.hasPaidPlan, trialStatus.isExpired, isPrivilegedUser, user]);

  const handleCloseModal = () => {
    setShowTrialModal(false);
    sessionStorage.setItem('trial_modal_shown', 'true');
  };

  const handleCloseExpiryWarning = () => {
    setShowExpiryWarning(false);
    const userId = user?._id || user?.id || 'unknown';
    const days = trialStatus.daysRemaining;
    const storageKey = `plan_expiry_warned_${userId}_${days}d`;
    localStorage.setItem(storageKey, 'true');
  };

  // Calculate days expired
  const daysExpired = trialStatus.isExpired
    ? Math.max(0, trialStatus.daysUsed - (trialStatus.planDurationDays || 15))
    : 0;

  return (
    <div className="app-layout">
      <Navbar />
      <AnnouncementModal />
      <div className="app-body">
        <Sidebar />
        <main className="app-content">{children}</main>
      </div>

      {/* Show trial modal for active trial users */}
      <TrialPlanModal
        isOpen={showTrialModal}
        onClose={handleCloseModal}
        createdAt={user?.createdAt || profile?.createdAt}
        currentPlan={user?.plan || profile?.plan}
        planExtraDays={profile?.planExtraDays ?? user?.planExtraDays ?? 0}
        initialPlanDays={profile?.initialPlanDays ?? user?.initialPlanDays ?? null}
      />

      {/* Plan expiry warning for Silver/Gold users at 15, 7, 6, 5, 4, 3, 2, 1 days */}
      <PlanExpiryWarningModal
        isOpen={showExpiryWarning && !isPaymentPage}
        onClose={handleCloseExpiryWarning}
        daysRemaining={trialStatus.daysRemaining}
        planTier={trialStatus.planTier}
        userId={user?._id || user?.id}
      />

      {/* Show blocking overlay when trial expired (except on payment page) */}
      <TrialExpiredOverlay 
        isVisible={trialStatus.isExpired && !isPaymentPage && !isPrivilegedUser}
        daysExpired={daysExpired}
        hasPaidPlan={trialStatus.hasPaidPlan}
        planTier={trialStatus.planTier}
      />

      {/* Show maintenance overlay when user is affected by maintenance mode */}
      <MaintenanceOverlay
        isVisible={maintenanceStatus.isAffected && !maintenanceStatus.loading}
        title={maintenanceStatus.title}
        message={maintenanceStatus.message}
        estimatedEndTime={maintenanceStatus.estimatedEndTime}
      />

      {/* AI Assistant floating button + panel */}
      <AiAssistant />

      {/* Contact center floating button */}
      <ContactCenter />
    </div>
  );
};

// Root redirect component - handles initial routing based on auth state
// Preserves query params (e.g. ?announcementId=...) so push notification deep links
// still work when the app was closed and SW opens "/?announcementId=xxx".
const RootRedirect = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const location = useLocation();
  const search = location.search || "";
  if (isAuthenticated) {
    // Keep announcement deep link param when forwarding to dashboard
    return <Navigate to={`/dashboard${search}`} replace />;
  }
  return <Navigate to={`/login${search}`} replace />;
};

const AppRoutes = () => {
  return (
    <Router>
      {/* Step-by-step back navigation: one press closes one overlay layer,
          then goes one page back. Handles browser back, touch hardware back,
          swipe-back gesture, Escape and native back buttons. */}
      <SmartBackHandler />
      <AuthInitializer>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-email"
          element={<VerifyEmail />}
        />


        {/* Private Routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Projects />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/events"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Event />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/team"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Team />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/services"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Services />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/quotations"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <GoldPlanRoute featureName="Quotations">
                  <Quotations />
                </GoldPlanRoute>
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <GoldPlanRoute featureName="Billing">
                  <Billing />
                </GoldPlanRoute>
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/payment"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Payment />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/policy"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <GoldPlanRoute featureName="Policy">
                  <Policy />
                </GoldPlanRoute>
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/notes"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Note />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/checklists"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <ChecklistList />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<Navigate to="/admin/plans" replace />} />
          <Route path="plans" element={<PlanManagement />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="plan-settings" element={<PlanSettings />} />
          <Route path="signup-plans" element={<SignupPlanControl />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="site-settings" element={<SiteSettings />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="security" element={<Security />} />
          <Route
            path="ai-assistant"
            element={
              <SuperAdminRoute>
                <AiAssistantSettings />
              </SuperAdminRoute>
            }
          />
          <Route 
            path="super-admin" 
            element={
              <SuperAdminRoute>
                <SuperAdminManagement />
              </SuperAdminRoute>
            } 
          />
        </Route>

        {/* Redirect root to dashboard or login based on auth state */}
        <Route path="/" element={<RootRedirect />} />

        {/* 404 fallback - redirect to login if not authenticated */}
        <Route path="*" element={<RootRedirect />} />
      </Routes>
      </AuthInitializer>
    </Router>
  );
};

export default AppRoutes;
